<template>
    <div>
        <v-form ref="tagForm" v-model="isFormValid">
            <v-row>
                <v-col cols="12">
                    <p class="text-body-2 mb-4">
                        {{ $t('Anna avainsana kaikilla kielillä.') }}
                    </p>
                </v-col>
            </v-row>
            <v-row v-for="language in availableLanguages" :key="language.code">
                <v-col cols="12">
                    <v-text-field v-model="tagTranslations[language.code]"
                        :label="$t('Avainsana') + ' (' + language.name + ')'" outlined :rules="[required]"
                        hide-details="auto" required>
                        <template v-slot:prepend>
                            <img :src="language.icon" style="max-width: 30px" />
                        </template>
                    </v-text-field>
                </v-col>
            </v-row>
        </v-form>
        <v-alert type="error" class="mt-2" v-if="errors">
            {{ errors }}
        </v-alert>
        <v-card-actions class="mt-4">
            <v-spacer></v-spacer>
            <v-btn color="grey darken-1" text @click="cancel">
                {{ $t('Peruuta') }}
            </v-btn>
            <v-btn v-if="mode == 'edit'" color="error" @click="deleteTag" :loading="isDeleting">
                {{ $t('Poista') }}
            </v-btn>
            <v-btn color="primary" @click="save" :loading="isSaving" :disabled="!isFormValid">
                {{ $t('Tallenna') }}
            </v-btn>

        </v-card-actions>
    </div>
</template>

<script>
import { ref, watch, computed, onMounted } from "@vue/composition-api";
import adminApi from "@/api/admin";
import { useTranslations } from "@/@core/libs/i18n";
import useAuth from "@/useAuth";
import { required } from "@/@core/utils/validation";
import { i18n } from "@/plugins/i18n";

export default {
    name: "TagForm",
    props: {
        tag: {
            type: Object,
            default: null
        },
        mode: {
            type: String,
            default: 'create', // 'create' or 'edit'
            validator: value => ['create', 'edit'].includes(value)
        },
        initialTitle: {
            type: String,
            default: ''
        }
    },
    setup(props, { emit }) {
        const { t } = useTranslations();
        const { tenant } = useAuth();
        const tagTranslations = ref({});
        const isSaving = ref(false);
        const isFormValid = ref(false);
        const tagForm = ref(null);
        const isDeleting = ref(false);
        const errors = ref("");

        // Get available languages from tenant
        const availableLanguages = computed(() => {
            return tenant.value?.languages || [];
        });

        // Initialize translations when component mounts or tag changes
        const initializeTranslations = () => {
            tagTranslations.value = {};

            if (props.mode === 'edit' && props.tag) {
                // Editing existing tag - populate with existing translations
                availableLanguages.value.forEach(lang => {
                    const existingTranslation = props.tag.translations?.[lang.code];
                    tagTranslations.value[lang.code] = existingTranslation?.title || '';
                });
            } else {
                // Creating new tag - initialize with initial title for primary language
                availableLanguages.value.forEach(lang => {
                    if (lang.code === i18n.locale) {
                        tagTranslations.value[lang.code] = props.initialTitle;
                    }
                    else {
                        tagTranslations.value[lang.code] = '';
                    }
                });
            }
        };

        // Watch for changes in tag prop
        watch(() => props.tag, () => {
            initializeTranslations();
        }, { immediate: true });

        // Watch for changes in mode
        watch(() => props.mode, () => {
            initializeTranslations();
        });


        const cancel = () => {
            tagTranslations.value = {};
            emit('cancel');
        };

        const deleteTag = async () => {
            errors.value = "";
            if (props.mode === 'edit' && props.tag) {
                if (confirm(i18n.t('Haluatko varmasti poistaa avainsanan?'))) {
                    isDeleting.value = true;
                    const res = await adminApi.deleteTag(props.tag.id);
                    if (res.ok) {
                        emit('delete');
                    }
                    else {
                        errors.value = res.data;
                    }
                    isDeleting.value = false;
                }
            }
        }

        const save = async () => {
            errors.value = "";
            // Validate the form before proceeding
            if (!tagForm.value.validate()) {
                return;
            }

            isSaving.value = true;
            try {
                // Prepare translations object with only non-empty values
                const translations = {};
                Object.keys(tagTranslations.value).forEach(langCode => {
                    const translation = tagTranslations.value[langCode];
                    if (translation && translation.trim().length > 0) {
                        translations[langCode] = { title: translation.trim() };
                    }
                });

                let response;
                if (props.mode === 'edit' && props.tag) {
                    // Update existing tag
                    response = await adminApi.updateTag(props.tag.id, { translations });
                } else {
                    // Create new tag
                    response = await adminApi.createTag({ translations });
                }

                if (response.ok) {
                    emit('save', response.data);
                    tagTranslations.value = {};
                } else {
                    errors.value = res.data;
                }
            } catch (error) {
                errors.value = error;
            } finally {
                isSaving.value = false;
            }
        };

        return {
            tagTranslations,
            isSaving,
            availableLanguages,
            isFormValid,
            tagForm,
            required,
            deleteTag,
            isDeleting,
            cancel,
            save,
            errors
        };
    }
};
</script>
